const _normalizeClusterChildren = (src: any[]) => {
  return src?.map((cluster: any) => ({
    ...cluster,
    children: (cluster.children || cluster.values || []).map((c: any) =>
      typeof c === 'string' ? { name: c } : c
    )
  })) || [];
};

const _normalizeGradCluster = (gradObj: any) => {
  if (!gradObj) return gradObj;
  if (Array.isArray(gradObj)) return _normalizeClusterChildren(gradObj);
  const res: any = {};
  Object.keys(gradObj).forEach(k => {
    res[k] = _normalizeClusterChildren(gradObj[k]);
  });
  return res;
};

const _getInstituteClusters = (masterData: any) => {
  const rawClusters = masterData?.topCollegeClusters || masterData?.topCollegeCluster || [];
  return _normalizeClusterChildren(rawClusters);
};

export const getClusteredInstitutes = (institutions: any[], masterData: any): any[] => {
  if (!institutions || institutions.length === 0) return [];

  const clusters = _getInstituteClusters(masterData);
  if (clusters.length === 0) return institutions;

  const result: any[] = [];

  const getItemName = (item: any) =>
    typeof item === 'string' ? item : (item?.name || item?.value || item);

  const itemsSet = new Set(institutions.map(getItemName));

  const clusterNames = new Set(clusters.map((c: any) => c.name));
  const allClusterChildren = new Set<string>();

  clusters.forEach((cluster: any) => {
    if (!cluster?.children?.length) return;
    cluster.children.forEach((c: any) => allClusterChildren.add(c.name));
  });

  /** Child institutes already represented by a full cluster or an emitted partial chip */
  const representedChildNames = new Set<string>();

  const pushFullCluster = (cluster: any) => {
    const originalClusterItem = institutions.find(
      (i) => getItemName(i) === cluster.name
    );
    result.push(originalClusterItem || cluster.name);
    cluster.children.forEach((c: any) => representedChildNames.add(c.name));
  };

  const pushPartialChild = (childName: string) => {
    if (representedChildNames.has(childName)) return;
    const originalItem = institutions.find(
      (i) => getItemName(i) === childName
    );
    result.push(originalItem || childName);
    representedChildNames.add(childName);
  };

  // Fully selected clusters first so shared institutes are not also emitted as partials
  clusters.forEach((cluster: any) => {
    if (!cluster?.children?.length) return;

    const childNames = cluster.children.map((c: any) => c.name);
    const selectedChildren = childNames.filter((name: string) =>
      itemsSet.has(name)
    );

    if (selectedChildren.length === 0) return;

    const isFullySelected = selectedChildren.length === childNames.length;
    if (isFullySelected) {
      pushFullCluster(cluster);
    }
  });

  // Partial selections: skip children already covered by another cluster or a prior partial emit
  clusters.forEach((cluster: any) => {
    if (!cluster?.children?.length) return;

    const childNames = cluster.children.map((c: any) => c.name);
    const selectedChildren = childNames.filter((name: string) =>
      itemsSet.has(name)
    );

    if (selectedChildren.length === 0) return;

    const isFullySelected = selectedChildren.length === childNames.length;
    if (isFullySelected) return;

    selectedChildren.forEach((childName: string) => {
      pushPartialChild(childName);
    });
  });

  // Standalone institutions (not part of any cluster definition)
  institutions.forEach((item) => {
    const name = getItemName(item);

    const isCluster = clusterNames.has(name);
    const isChild = allClusterChildren.has(name);

    if (!isCluster && !isChild) {
      result.push(item);
    }
  });

  return result;
};

export const removeInstituteOrCluster = (institutions: any[], removedItem: string, masterData: any): any[] => {
  if (!institutions || institutions.length === 0) return [];
  if (!removedItem) return institutions;

  const clusters = _getInstituteClusters(masterData);
  const getItemName = (item: any) => typeof item === 'string' ? item : (item?.name || item?.value || item);

  if (clusters.length === 0) {
    return institutions.filter((item) => getItemName(item) !== removedItem);
  }

  const cluster = clusters.find((c: any) => c.name === removedItem);

  if (cluster && cluster.children && Array.isArray(cluster.children)) {
    const clusterChildNamesSet = new Set(cluster.children.map((child: any) => child.name));
    return institutions.filter((item) => {
      const itemName = getItemName(item);
      return !clusterChildNamesSet.has(itemName) && itemName !== removedItem;
    });
  }

  // Removing a child: if the list stores a cluster chip, expand it to the remaining members
  const expanded: any[] = [];
  for (const item of institutions) {
    const itemName = getItemName(item);
    if (itemName === removedItem) continue;

    const asCluster = clusters.find((c: any) => c.name === itemName);
    if (
      asCluster?.children?.length &&
      asCluster.children.some((child: any) => child.name === removedItem)
    ) {
      asCluster.children.forEach((ch: any) => {
        if (ch.name !== removedItem) {
          const orig = institutions.find((i) => getItemName(i) === ch.name);
          expanded.push(orig || ch.name);
        }
      });
      continue;
    }

    expanded.push(item);
  }

  return expanded;
};

const _deriveCourseType = (key: string): string | undefined => {
  if (!key) return undefined;
  const k = key.toLowerCase();
  if (k === 'no_ug' || k.includes('noug')) return 'NO_UG';
  if (k.includes('ppg')) return 'PPG';
  if (k.includes('pg')) return 'PG';
  if (k.includes('ug')) return 'UG';
  return undefined;
};

const _getEducationClusters = (masterData: any, channel?: number, courseType?: string) => {
  const ch = channel ?? masterData?.channelId ?? masterData?.channel;
  let eduSource = masterData?.gradCluster || [];

  if (eduSource && typeof eduSource === 'object' && !Array.isArray(eduSource)) {
    const normalizedGrad = _normalizeGradCluster(eduSource);
    if (courseType) {
      const resolvedCourseType = _deriveCourseType(courseType) || courseType;
      eduSource = normalizedGrad[resolvedCourseType.toUpperCase()] || [];
    } else {
      eduSource = Object.values(normalizedGrad).flat();
    }
  } else if (Array.isArray(eduSource)) {
    eduSource = _normalizeClusterChildren(eduSource);
  }

  if (ch === 2 && masterData?.eduStreamData) eduSource = _normalizeClusterChildren(masterData.eduStreamData);
  if (ch === 4 && masterData?.educationData) eduSource = _normalizeClusterChildren(masterData.educationData);
  return eduSource;
};

export const normalizeMasterDataForEducationFilter = (rawMasterData: any, channel?: number) => {
  if (!rawMasterData) return rawMasterData;
  const ch = channel || rawMasterData.channelId || rawMasterData.channel || 1;
  const normalizedCollegeCluster = rawMasterData.topCollegeClusters
    ? _normalizeClusterChildren(rawMasterData.topCollegeClusters)
    : _normalizeClusterChildren(rawMasterData.topCollegeCluster || []);

  return {
    ...rawMasterData,
    channelId: ch,
    topEducationCluster: _normalizeClusterChildren(rawMasterData.topEducationCluster || []),
    gradCluster: _normalizeGradCluster(rawMasterData.gradCluster),
    eduStreamData: _normalizeClusterChildren(rawMasterData.eduStreamData),
    educationData: _normalizeClusterChildren(rawMasterData.educationData),
    topCollegeCluster: normalizedCollegeCluster,
  };
};

export const getClusteredDegrees = (degrees: any[], masterData: any, channel?: number, courseType?: string): any[] => {
  if (!degrees || degrees.length === 0) return [];
  
  const clusters = _getEducationClusters(masterData, channel, courseType);
  if (!clusters || clusters.length === 0) return degrees;

  return degrees.map(deg => {
    if (typeof deg === 'string') return deg;

    const degreeName = deg.degree || deg.name;
    const selectedSpecs = deg.specializations || [];
    
    const clusterDef = clusters.find((c: any) => c.name === degreeName);
    
    if (!clusterDef || !clusterDef.children || clusterDef.children.length === 0) {
      return deg; 
    }

    const allExpectedSpecs = clusterDef.children.map((child: any) => child.name);

    const hasAll = allExpectedSpecs.every((spec: string) => selectedSpecs.includes(spec));
    if (hasAll) {
      return { ...deg, specializations: [] };
    }

    return deg;
  });
};

export const removeDegreeOrCluster = (degrees: any[], removedItem: string, masterData: any, channel?: number, courseType?: string): any[] => {
  if (!degrees || degrees.length === 0) return [];
  if (!removedItem) return degrees;

  const clusters = _getEducationClusters(masterData, channel, courseType);

  if (clusters.length === 0) {
    return degrees.filter(d => {
      const itemName = typeof d === 'string' ? d : (d.degree || d.name);
      return itemName !== removedItem;
    });
  }

  const cluster = clusters.find((c: any) => c.name === removedItem);

  if (cluster && cluster.children && Array.isArray(cluster.children)) {
    const clusterChildNamesSet = new Set(cluster.children.map((child: any) => child.name));
    return degrees.filter((d) => {
      const itemName = typeof d === 'string' ? d : (d.degree || d.name);
      return !clusterChildNamesSet.has(itemName) && itemName !== removedItem;
    });
  }

  return degrees.filter((d) => {
    const itemName = typeof d === 'string' ? d : (d.degree || d.name);
    return itemName !== removedItem;
  });
};

/**
 * Groups education levels into clusters if possible.
 */
export const generateEducationLevelCluster = (
  levels: any[],
  masterData: any,
  channel?: number,
  courseType?: string
): any[] => {
  return getClusteredDegrees(levels, masterData, channel, courseType);
};

/**
 * Removes an education level or a whole cluster.
 */
export const removeEducationLevelCluster = (
  levels: any[],
  removedItem: string,
  masterData: any,
  channel?: number,
  courseType?: string
): any[] => {
  return removeDegreeOrCluster(levels, removedItem, masterData, channel, courseType);
};

export const getClusteredEducation = (educationObj: any, masterData: any, channel?: number) => {
  if (!educationObj) return {};
  const result: any = { ...educationObj };
  
  Object.keys(educationObj).forEach(key => {
    const levelObj = educationObj[key];
    if (levelObj && typeof levelObj === 'object' && !Array.isArray(levelObj)) {
      const updatedLevel: any = { ...levelObj };
      const derivedCourseType = _deriveCourseType(key);
      
      if (Array.isArray(updatedLevel.degrees)) {
        updatedLevel.degrees = getClusteredDegrees(updatedLevel.degrees, masterData, channel, derivedCourseType);
      }
      if (Array.isArray(updatedLevel.institutions)) {
        updatedLevel.institutions = getClusteredInstitutes(updatedLevel.institutions, masterData);
      }
      result[key] = updatedLevel;
    }
  });
  return result;
};

export const removeEducation = (
  educationObj: any,
  levelKey: string,
  removeType: 'degree' | 'institute',
  removedItem: string,
  masterData: any,
  channel?: number,
  courseType?: string
) => {
  const updated = { ...educationObj };
  
  if (updated[levelKey] && typeof updated[levelKey] === 'object') {
    const updatedLevel = { ...updated[levelKey] };
    const effectiveCourseType = courseType || _deriveCourseType(levelKey);
    
    if (removeType === 'degree') {
      updatedLevel.degrees = removeDegreeOrCluster(updatedLevel.degrees || [], removedItem, masterData, channel, effectiveCourseType);
    } else if (removeType === 'institute') {
      updatedLevel.institutions = removeInstituteOrCluster(updatedLevel.institutions || [], removedItem, masterData);
    }

    updated[levelKey] = updatedLevel;
  }

  return {
    updatedObject: updated,
    clusteredObject: getClusteredEducation(updated, masterData, channel)
  };
};
